import _ from 'lodash';
import i18n from 'i18next';
import { isPayAtTablePlatform, PLATFORMS } from './app';

Mobi2Go._class = Mobi2Go._class || {};

const FREEZE_TIME = Mobi2Go.helpers.getQueryStringAsObject().freeze_store_time;
const { mixinEvents } = Mobi2Go.app;
const { Price } = Mobi2Go._class.Common;
const { MapsService } = Mobi2Go;
const { DATE_FORMAT } = Mobi2Go.app;
const { DAYS } = Mobi2Go.app;
const { OperatingHours } = Mobi2Go._class;
const { Day } = Mobi2Go._class;
const PROPERTIES_TO_LOAD = [
    'address',
    'online',
    'offline_message',
    'customer_message_html',
    'order_methods',
    'opening_hours',
    'wait_times',
    'current_time',
    'accept_orders_until_closed',
    'delivery_areas',
    'delivery_minimum_order_products_total',
    'payment_methods',
    'menus',
    'status',
    'offline_message',
    'menu_restrictions',
    'trading_name',
    'sales_tax',
    'tax_number',
    'is_storefront_locked_out',
    'holidays',
    'group_tabs_enabled',
    'pay_at_table_enabled',
    'pay_at_table_configured',
];

const Location = (Mobi2Go._class.Location = function(data) {
    if (!(this instanceof Mobi2Go._class.Location)) {
        return new Mobi2Go._class.Location(data);
    }

    this.init(data);
});

_.extend(Location.prototype, {
    /**-------------------------------------------------------------------------
     */
    init(data) {
        this.mute = true;
        mixinEvents(`Location#${data.id}`, this);

        this._available_order_dates = {};
        this._available_order_times = {};
        this.use_new_opening_hours = false;

        this.set(data);

        this._has_loaded_recently = false;
        this.distance_to_customer = null;
        this.distance_to_customer_formatted = null;

        if (MapsService && MapsService.getCoordinates()) {
            this.setDistanceFromPoint(MapsService.getCoordinates());
        }

        Mobi2Go.bind(
            'MapsService',
            'coordinatesAvailable',
            _.bind(this.setDistanceFromPoint, this)
        );
    },

    /**-------------------------------------------------------------------------
     */
    get() {
        return _.pick(this, ['id', 'name']);
    },

    /**-------------------------------------------------------------------------
     */
    setDistanceFromPoint(coordinates) {
        this.distance_to_customer =
            Math.round(
                Mobi2Go.MapsService.getDistanceBetweenPoints(
                    coordinates,
                    this.address
                ) * 100
            ) / 100;

        if (this.distance_to_customer === 0) {
            this.distance_to_customer_formatted = ''; // We don't want to show "0m away"
        } else if (this.distance_to_customer < 1) {
            // 500m
            this.distance_to_customer_formatted = `${this.distance_to_customer *
                1000}m`;
        } else if (this.distance_to_customer < 10) {
            // 5.5km
            this.distance_to_customer_formatted = `${Math.round(
                this.distance_to_customer * 10
            ) / 10}km`;
        } else {
            // 25km
            this.distance_to_customer_formatted = `${Math.round(
                this.distance_to_customer
            )}km`;
        }
    },

    /*--------------------------------------------------------------------------
     */
    set(data) {
        _.extend(this, _.omit(data, ['menu_restrictions', 'opening_hours']));

        if (data.opening_hours !== undefined) {
            this.setOpeningHours(data.opening_hours, data.current_time);
            generateAbbrOpeningHours.call(this);
        }

        if (data.current_time !== undefined) {
            this.setCurrentTime(data.current_time);
        }

        if (data.menu_restrictions !== undefined) {
            this.setMenuRestrictions(data.menu_restrictions);
        }

        if (data.delivery_minimum !== undefined) {
            this.delivery_minimum = new Price(data.delivery_minimum);
        }

        if (data.delivery_areas !== undefined) {
            this._delivery_suburb_ids = _.map(
                data.delivery_areas.suburbs,
                'suburb_id'
            );
        }

        if (data.group_tabs_enabled !== undefined) {
            this.group_tabs_enabled = data.group_tabs_enabled;
        }

        if (data.pay_at_table_enabled !== undefined) {
            this.pay_at_table_enabled = data.pay_at_table_enabled;
        }

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    setCurrentTime(time) {
        this.current_time = moment.parseZone(time);

        if (FREEZE_TIME) {
            const time_to_freeze = moment.parseZone(
                FREEZE_TIME.replace(' ', '+')
            ); // getQueryStringAsObject replaces pluses with spaces
            if (
                time_to_freeze.isAfter(this.current_time) ||
                time_to_freeze.isSame(this.current_time)
            ) {
                this.current_time = time_to_freeze;
            }
        }

        this.current_time.today = this.current_time.format(DATE_FORMAT);
        this.current_time.tomorrow = this.current_time
            .clone()
            .add(1, 'days')
            .format(DATE_FORMAT);

        this.clearAvailableOrderDatesAndTimesCache();
        this.updateOpeningHoursUtcOffset();

        if (!FREEZE_TIME) {
            startAutoUpdatingCurrentTime(this);
        }

        this.trigger('updateCurrentTime', this.current_time);
    },

    clearAvailableOrderDatesAndTimesCache() {
        this._available_order_dates = {};
        this._available_order_times = {};
    },

    updateOpeningHoursUtcOffset() {
        const utc_offset = this.current_time.utcOffset();
        const that = this;
        _.each(['pickup', 'delivery', 'eatin'], function(order_method) {
            if (that.use_new_opening_hours) {
                that.opening_hours[order_method].setUtcOffset(utc_offset);
            }
        });
    },

    /*--------------------------------------------------------------------------
     * Loads properties from backend
     */
    load(callback) {
        const that = this;

        if (!this.hasLoadedRecently() && document.hidden !== true) {
            Mobi2Go.app.serverRequest({
                endpoint: ['locations', this.id],
                from_cdn: Mobi2Go.app.feature_flags?.load_locations_from_cdn,
                data: { fields: PROPERTIES_TO_LOAD.join(',') },
                api_version: 1,
                with_credentials: false,
                success(data) {
                    that.set(data).trigger('load');
                    that._has_loaded_recently = true;
                    _.isFunction(callback) && callback();

                    _.delay(function() {
                        that._has_loaded_recently = false;
                    }, 48000);
                },
            });
        } else {
            _.isFunction(callback) && _.defer(callback);
        }
    },

    /*--------------------------------------------------------------------------
     */
    hasLoadedRecently() {
        return this._has_loaded_recently;
    },

    /**-------------------------------------------------------------------------
     */
    startAutoLoading() {
        const load = _.bind(this.load, this);
        this.load_timeout = setInterval(load, 50000);
    },

    /**-------------------------------------------------------------------------
     */
    stopAutoLoading() {
        clearTimeout(this.load_timeout);
    },

    /*--------------------------------------------------------------------------
     * Menu Restrictions
     * Restrict menu items from being able to be orders. Used for 'sold out'
     * and 'hidden' menu items
     */
    isProductRestricted(product, date) {
        const has_restriction = !!this.getProductRestriction(product, date);
        const is_restricted_by_modifier_groups = this.isProductRestrictedByModifierGroups(
            product,
            date
        );

        return has_restriction || is_restricted_by_modifier_groups;
    },
    isProductSoldOut(product, date) {
        const product_restriction = this.getProductRestriction(product, date);

        if (
            (product_restriction &&
                product_restriction.reason === 'Sold Out') ||
            this.isProductRestrictedByModifierGroups(product, date)
        ) {
            return true;
        }
        return false;
    },
    /**
     * Determines whether a product is restricted because its child modifiers
     * are restricted to the extent that the product's modifier selection
     * requirements cannot be met
     */
    isProductRestrictedByModifierGroups(product, date) {
        const size_options = product.getSizeOptions();
        const has_restricted_size_group =
            size_options.length &&
            size_options.every(size_modifier =>
                this.isModifierRestricted(size_modifier, date)
            );

        if (has_restricted_size_group) {
            return true;
        }

        const at_least_one_group_is_restricted = product
            .getModifierGroups()
            .toArray()
            .some(modifier_group =>
                this.isModifierGroupRestricted(modifier_group, date)
            );

        if (at_least_one_group_is_restricted) {
            return true;
        }

        return false;
    },
    isModifierGroupRestricted(modifier_group, date) {
        if (modifier_group.selection_requirement === 0) {
            return false;
        }

        const that = this;
        const available_modifiers = modifier_group
            .getModifiers()
            .filter(function(modifier) {
                return !that.isModifierRestricted(modifier, date);
            });
        const available_products = modifier_group
            .getProducts()
            .filter(function(product) {
                return !that.isProductRestricted(product, date);
            });

        const has_available_quantity_modifiers = _.some(
            available_modifiers,
            function(modifier) {
                return modifier.type === 'quantity';
            }
        );

        return (
            (available_modifiers.length === 0 &&
                available_products.length === 0) ||
            (!has_available_quantity_modifiers && // A single quantity modifier can fulfil selection requirements
                available_products.length === 0 &&
                available_modifiers.length <
                    modifier_group.selection_requirement)
        );
    },
    isProductHidden(product, date) {
        const product_restriction = this.getProductRestriction(product, date);

        if (product_restriction && product_restriction.reason === 'Hidden') {
            return true;
        }
        return false;
    },
    isModifierRestricted(modifier, date) {
        return !!this.getModifierRestriction(modifier, date);
    },
    isModifierSoldOut(modifier, date) {
        const modifier_restriction = this.getModifierRestriction(
            modifier,
            date
        );

        return !!(
            modifier_restriction && modifier_restriction.reason === 'Sold Out'
        );
    },
    isModifierHidden(modifier, date) {
        const modifier_restriction = this.getModifierRestriction(
            modifier,
            date
        );

        return !!(
            modifier_restriction && modifier_restriction.reason === 'Hidden'
        );
    },
    setMenuRestrictions(menu_restrictions) {
        const utc_offset = this.current_time.utcOffset();
        const parsed_menu_restrictions = _.map(menu_restrictions, function(
            restriction
        ) {
            const parsed_menu_restriction = _.clone(restriction);
            parsed_menu_restriction.starts_at = moment(
                restriction.starts_at
            ).utcOffset(utc_offset);
            parsed_menu_restriction.ends_at = moment(
                restriction.ends_at
            ).utcOffset(utc_offset);
            return parsed_menu_restriction;
        });

        if (_.isEqual(parsed_menu_restrictions, this.menu_restrictions)) {
            return;
        }

        this.menu_restrictions = parsed_menu_restrictions;
        this.trigger('updateMenuRestrictions', this.menu_restrictions);
    },
    getMenuRestriction(menu, date) {
        return this.getItemRestriction('menu', menu, date);
    },
    getCategoryRestriction(category, date) {
        return this.getItemRestriction('category', category, date);
    },
    getProductRestriction(product, date) {
        return this.getItemRestriction('product', product, date);
    },
    getModifierRestriction(modifier, date) {
        return this.getItemRestriction('modifier', modifier, date);
    },
    getItemRestriction(item_type, item, date) {
        if (!this.menu_restrictions) {
            return null;
        }

        const utc_offset = this.current_time.utcOffset();
        const current_date = date
            ? moment(date).utcOffset(utc_offset)
            : this.current_time;

        const restriction = this.menu_restrictions.find(
            r =>
                r.item_type === item_type &&
                r.item.id === item.id &&
                current_date.isBetween(r.starts_at, r.ends_at)
        );

        return restriction || null;
    },

    /*--------------------------------------------------------------------------
     */
    isActive() {
        return this.status === 'active';
    },

    /*--------------------------------------------------------------------------
     */
    isInactive() {
        return this.status === 'inactive';
    },

    /*--------------------------------------------------------------------------
     */
    isHidden() {
        return this.status === 'hidden';
    },

    /*--------------------------------------------------------------------------
     * Store hasn't been paying their bills
     */
    isLockedOut() {
        return this.is_storefront_locked_out;
    },

    /*--------------------------------------------------------------------------
     * Return OperatingHours' compatible overrides given the location's holidays.
     */
    getOperatingHoursOverrides() {
        const that = this;
        const overrides = {};
        _.each(_.keys(this.holidays), function(date) {
            const holiday = that.holidays[date];
            if (holiday.hours !== null) {
                overrides[date] = holiday.hours;
            }
        });
        return overrides;
    },

    setOpeningHours(opening_hours, current_time) {
        const that = this;
        this.opening_hours = {};

        const utc_offset = moment.parseZone(current_time).utcOffset();
        _.each(['pickup', 'delivery', 'eatin'], function(order_method) {
            if (order_method in opening_hours) {
                const opening_hours_for_order_method =
                    opening_hours[order_method];
                if (opening_hours_for_order_method.is_experimental_format) {
                    delete opening_hours_for_order_method.is_experimental_format;
                    opening_hours_for_order_method.overrides = that.getOperatingHoursOverrides();
                    that.use_new_opening_hours = true;
                    const oh = OperatingHours.create(
                        opening_hours_for_order_method
                    );
                    oh.setUtcOffset(utc_offset);
                    that.opening_hours[order_method] = oh;
                } else {
                    that.opening_hours[
                        order_method
                    ] = opening_hours_for_order_method;
                }
            }
        });
    },

    /*--------------------------------------------------------------------------
     * Returns whether the store is open for any order method
     */
    isOpen(datetime, order_method) {
        if (!order_method) {
            const available_order_methods = this.getAvailableOrderMethods();
            let is_open = false;

            _.each(available_order_methods, method => {
                if (is_open || this.isOpenForMethod(method, datetime)) {
                    is_open = true;
                }
            });

            return is_open;
        }
        return this.isOpenForMethod(order_method, datetime);
    },

    /*--------------------------------------------------------------------------
     * Return whether the store is open for a order method
     */
    isOpenForMethod(order_method, datetime) {
        datetime = datetime ? moment(datetime) : this.current_time;

        if (!this.isOrderMethodAvailable(order_method)) {
            return false;
        }

        if (this._is_open) {
            if (this._is_open[order_method + +datetime]) {
                return this._is_open[order_method + +datetime];
            }
        } else {
            this._is_open = {};
        }

        let opening_hours;
        let is_open = false;

        if (this.use_new_opening_hours) {
            opening_hours = this.opening_hours[order_method];

            is_open = opening_hours ? opening_hours.isOpenAt(datetime) : false;
        } else {
            opening_hours = this.getOpeningHoursForDate(order_method, datetime);

            if (!opening_hours.is_open) {
                is_open = false;
            } else {
                is_open =
                    datetime.isAfter(opening_hours.opening_time) &&
                    datetime.isBefore(opening_hours.closing_time);
            }
        }

        return (this._is_open[order_method + +datetime] = is_open);
    },

    /*--------------------------------------------------------------------------
     */
    getClosingTimeSentence(method) {
        const current_time = this.current_time.clone();
        const available_order_methods = method
            ? [method]
            : this.getAvailableOrderMethods();
        let i;
        let order_method;
        let opening_hours;
        let opening_hours_for_date;

        if (
            (!method && this.isOpen()) ||
            (method && this.isOpenForMethod(method))
        ) {
            for (i in available_order_methods)
                if (available_order_methods.hasOwnProperty(i)) {
                    order_method = available_order_methods[i];
                    if (this.isOpenForMethod(order_method)) {
                        if (this.use_new_opening_hours) {
                            opening_hours = this.opening_hours[order_method];

                            if (opening_hours) {
                                opening_hours_for_date = opening_hours.forDate(
                                    this.current_time
                                );
                                const last_time_range = _.last(
                                    opening_hours_for_date.getTimeRanges()
                                );

                                return i18n.t('location:closes_time_sentence', {
                                    time: i18n.t('common:time', {
                                        time: last_time_range.end,
                                    }),
                                });
                            }
                        } else {
                            opening_hours = this.getOpeningHoursForDate(
                                order_method
                            );
                            const { closing_time } = opening_hours;

                            return i18n.t('location:closes_time_sentence', {
                                time: i18n.t('common:time', {
                                    time: closing_time,
                                }),
                            });
                        }
                    }
                }
        } else {
            for (i in available_order_methods) {
                if (available_order_methods.hasOwnProperty(i)) {
                    order_method = available_order_methods[i];
                    if (!this.isOpenForMethod(order_method)) {
                        let next_available_time = this.getAsapOrderDatetime(
                            order_method
                        );

                        if (next_available_time) {
                            next_available_time = next_available_time
                                .clone()
                                .subtract(
                                    this.wait_times[order_method],
                                    'minutes'
                                );

                            return i18n.t('location:opens_time_sentence', {
                                time: i18n.t('common:time', {
                                    time: next_available_time,
                                }),
                            });
                        }

                        const tomorrow = current_time.clone().add(1, 'd');
                        const available_order_dates = this.getAvailableOrderDates(
                            order_method,
                            7
                        );
                        for (i in available_order_dates) {
                            if (available_order_dates.hasOwnProperty(i)) {
                                const is_tomorrow = tomorrow.isSame(
                                    available_order_dates[i],
                                    'day'
                                );

                                if (this.use_new_opening_hours) {
                                    opening_hours = this.opening_hours[
                                        order_method
                                    ];
                                    if (opening_hours) {
                                        opening_hours_for_date = opening_hours.forDate(
                                            available_order_dates[i]
                                        );

                                        if (
                                            opening_hours_for_date.count() > 0
                                        ) {
                                            const first_time_range = _.first(
                                                opening_hours_for_date.getTimeRanges()
                                            );
                                            if (is_tomorrow) {
                                                return i18n.t(
                                                    'location:opens_tomorrow_time_sentence',
                                                    {
                                                        time: i18n.t(
                                                            'common:time',
                                                            {
                                                                time:
                                                                    first_time_range.start,
                                                            }
                                                        ),
                                                    }
                                                );
                                            }
                                            return i18n.t(
                                                'location:opens_time_sentence',
                                                {
                                                    time: i18n.t(
                                                        'common:time_and_day',
                                                        {
                                                            date: first_time_range.start.toDateTime(
                                                                available_order_dates[
                                                                    i
                                                                ]
                                                            ),
                                                        }
                                                    ),
                                                }
                                            );
                                        }
                                    }
                                } else {
                                    opening_hours = this.getOpeningHoursForDate(
                                        order_method,
                                        available_order_dates[i]
                                    );

                                    if (opening_hours.is_open) {
                                        if (is_tomorrow) {
                                            return i18n.t(
                                                'location:opens_tomorrow_time_sentence',
                                                {
                                                    time: i18n.t(
                                                        'common:time',
                                                        {
                                                            time:
                                                                opening_hours.opening_time,
                                                        }
                                                    ),
                                                }
                                            );
                                        }
                                        return i18n.t(
                                            'location:opens_time_sentence',
                                            {
                                                time: i18n.t(
                                                    'common:time_and_day',
                                                    {
                                                        date:
                                                            opening_hours.opening_time,
                                                    }
                                                ),
                                            }
                                        );
                                    }
                                }
                            }
                        }

                        return '';
                    }
                }
            }
        }
    },

    /*--------------------------------------------------------------------------
     * Returns list of dates orders can be placed in the future
     */
    getAvailableOrderDates(order_method, num_future_dates) {
        if (!order_method) {
            return [];
        }

        const dates = [];
        let i;
        let opening_hours;
        let date;
        const num_dates =
            num_future_dates || this.scheduled_order_dates_limit || 90;
        let date_i;
        let continues = 0;
        let available_times;

        // Caching
        if (
            this._available_order_dates[order_method] &&
            this._available_order_dates[order_method][num_dates]
        ) {
            return this._available_order_dates[order_method][num_dates];
        }

        date_i = this.current_time.clone().startOf('day');

        for (i = 0; i < num_dates; i++) {
            date = date_i.clone();
            date.date_value = date.format(DATE_FORMAT);
            date.surcharge = this.getSurchargeForDate(date);

            available_times = this.getAvailableOrderTimes(order_method, date);

            if (this.use_new_opening_hours) {
                // No need to check the opening hours
                // Available order times is already derived from them
                date.is_closed =
                    available_times.length === 0 ||
                    this.isDateClosedForHoliday(date);
            } else {
                opening_hours = this.getOpeningHoursForDate(order_method, date);
                date.is_closed =
                    !opening_hours.is_open || this.isDateClosedForHoliday(date);
            }

            // No available times means that the store has closed for the day
            // Decrement iterator to allow 'Tomorrow' to show if the store
            // has `future_available_dates` = 1
            if (available_times.length === 0) {
                if (continues++ < 7) {
                    // Prevent infinite loop
                    i--;
                }
                date.is_closed = true;
            }

            switch (date.date_value) {
                case this.current_time.today:
                    if (
                        available_times.length &&
                        available_times[0].hour() >= 17
                    ) {
                        date.display_value = i18n.t('order.datetime:tonight');
                    } else {
                        date.display_value = i18n.t('order.datetime:today');
                    }

                    break;
                case this.current_time.tomorrow:
                    date.display_value = i18n.t('order.datetime:tomorrow');
                    break;
                default:
                    date.display_value = date.format('ddd, Do MMM');
            }

            dates.push(date);

            date_i.add(1, 'day');
        }

        if (!this._available_order_dates[order_method]) {
            this._available_order_dates[order_method] = {};
        }
        this._available_order_dates[order_method][num_dates] = dates;
        return dates;
    },

    /*--------------------------------------------------------------------------
     * Returns list of times orders can be placed in the future given an
     * order method and date.
     */
    getAvailableOrderTimes(order_method, date) {
        let times = []; // Available order times to return (moment objects)
        let opening_hours; // Opening hours of the date
        const asap_expected_at_interval = this.order_asap_expected_at_intervals[
            order_method
        ];
        let time_i; // moment object to iterate
        let wait_time = this.wait_times[order_method];

        // TODO: find a cleaner way to do this
        if (
            Mobi2Go.Order.getMethod() === order_method &&
            Mobi2Go.Order.getWaitTime() !== null
        ) {
            wait_time = Mobi2Go.Order.getWaitTime();
        }

        if (!order_method) {
            throw new TypeError('order method required');
        }

        if (!moment.isMoment(date)) {
            date = date || this.current_time.today;
            date = this.momentWithTimezone(date);
        }

        if (
            this._available_order_times[order_method] &&
            this._available_order_times[order_method][+date]
        ) {
            return this._available_order_times[order_method][+date];
        }

        if (this.use_new_opening_hours) {
            const that = this;
            const start_offset = wait_time;
            const end_offset = this.accept_orders_until_closed ? wait_time : 0;

            if (!this.opening_hours[order_method]) {
                return times;
            }

            opening_hours = this.opening_hours[order_method].cloneWithOffset(
                start_offset,
                end_offset
            );
            const from = this.current_time.clone();
            from.add(wait_time, 'minutes').seconds(0);
            const scheduled_expected_at_interval = this
                .order_scheduled_expected_at_intervals[order_method];
            times = opening_hours
                .getAvailableTimeSlotsForDate(
                    date,
                    from,
                    asap_expected_at_interval,
                    scheduled_expected_at_interval
                )
                .map(function(slot) {
                    return that.toOrderDatetime(slot);
                });
        } else {
            opening_hours = this.getOpeningHoursForDate(order_method, date);
            if (!opening_hours.is_open) {
                return times;
            }

            if (
                date.format(DATE_FORMAT) === this.current_time.today &&
                this.current_time > opening_hours.opening_time
            ) {
                time_i = this.current_time.clone(); // Start of iteration = now
            } else {
                time_i = opening_hours.opening_time; // Start of iteration = start of day
            }

            time_i.add(wait_time, 'minutes').seconds(0);
            time_i = roundMinutes(time_i, asap_expected_at_interval);

            if (this.accept_orders_until_closed) {
                // Allow times to be selected that are the closing time + wait time
                opening_hours.closing_time.add(wait_time, 'minutes');
            }

            while (
                time_i.isBefore(opening_hours.closing_time) ||
                time_i.isSame(opening_hours.closing_time)
            ) {
                times.push(this.toOrderDatetime(time_i));

                // Times beyond ASAP are rounded to 5 minutes
                if (times.length === 1 && asap_expected_at_interval !== 5) {
                    time_i = roundMinutes(time_i, 5);
                }

                time_i.add(5, 'minutes');
            }
        }

        if (!this._available_order_times[order_method]) {
            this._available_order_times[order_method] = {};
        }

        this._available_order_times[order_method][+date] = times;
        return times;
    },

    /*--------------------------------------------------------------------------
     */
    isDateClosedForHoliday(date) {
        return _.some(this.holidays, function(holiday) {
            return (
                holiday.type === 'CLOSING' && holiday.date === date.date_value
            );
        });
    },

    /*--------------------------------------------------------------------------
     */
    getSurchargeForDate(date) {
        const surcharge = _.find(this.holidays, function(holiday) {
            return (
                holiday.type === 'SURCHARGE' && holiday.date === date.date_value
            );
        });

        return surcharge ? surcharge.surcharge : null;
    },

    /*--------------------------------------------------------------------------
     * Helper
     */
    getAvailableOrderTimesAsStrings(order_method, date) {
        return _.map(
            this.getAvailableOrderTimes(order_method, date).slice(0, 5),
            function(time) {
                return time.format();
            }
        );
    },

    /*--------------------------------------------------------------------------
     * Returns 'ASAP' order time meaning the soonest available order time
     */
    getAsapOrderDatetime(order_method) {
        const current_time = this.current_time.clone();
        let available_times = this.getAvailableOrderTimes(
            order_method,
            current_time
        );
        if (available_times.length) {
            return available_times[0];
        }

        let x = 0;
        while (available_times.length === 0 && x < 30) {
            available_times = this.getAvailableOrderTimes(
                order_method,
                current_time.add(1, 'day')
            );
            x++;
        }

        if (available_times.length) {
            return available_times[0];
        }

        return undefined;
    },

    /**-------------------------------------------------------------------------
     */
    isAsapOrderDatetime(order_method, datetime) {
        const asap_time = this.getAsapOrderDatetime(order_method);
        if (!moment.isMoment(datetime)) {
            datetime = moment.parseZone(datetime);
        }

        return asap_time && asap_time.isSame(datetime);
    },

    /*--------------------------------------------------------------------------
     */
    getMinutesToAsapOrderDatetime(order_method) {
        const time = this.getAsapOrderDatetime(order_method);

        return time ? Math.max(1, time.diff(this.current_time, 'minutes')) : 0;
    },

    /**-------------------------------------------------------------------------
     */
    getFinalOrderTime(order_method, date) {
        return _.last(this.getAvailableOrderTimes(order_method, date));
    },

    /**-------------------------------------------------------------------------
     */
    getSecondsToFinalOrderTime(order_method, date) {
        const final_time = this.getFinalOrderTime(order_method, date);

        return final_time ? final_time.diff(this.current_time, 'seconds') : 0;
    },

    /**-------------------------------------------------------------------------
     */
    getSecondsToFinalOrderTimeCutOff(order_method, date) {
        let wait_time = this.wait_times[order_method];
        if (
            Mobi2Go.Order.getMethod() === order_method &&
            Mobi2Go.Order.getWaitTime() !== null
        ) {
            wait_time = Mobi2Go.Order.getWaitTime();
        }
        return (
            this.getSecondsToFinalOrderTime(order_method, date) - wait_time * 60
        );
    },

    /**---------------------------------------------------------------------
     * Whether it's possible to expect an order within the wait at the current
     * time
     */
    areNonScheduledOrdersAvailableForMethod(method) {
        const minutes_to_asap_order = this.getMinutesToAsapOrderDatetime(
            method
        );
        const threshold =
            this.wait_times[method] +
            this.order_asap_expected_at_intervals[method];

        return minutes_to_asap_order && minutes_to_asap_order <= threshold;
    },

    /*----------------------------------------------------------------------
     * Returns opening hours for a specific date
     */
    getOpeningHoursForDate(order_method, date) {
        let opening_hours;
        let day;
        if (!moment.isMoment(date)) {
            date = moment.parseZone(date);
        }

        if (!this.isOrderMethodAvailable(order_method)) {
            return {
                is_open: false,
                open_time: date,
                closing_time: date,
            };
        }

        day = date
            .clone()
            .locale('en')
            .format('dddd')
            .toLowerCase();
        opening_hours = this.opening_hours[order_method][day];

        return {
            is_open: opening_hours.is_open,
            opening_time: date
                .clone()
                .hours(opening_hours.open.split(':')[0])
                .minutes(opening_hours.open.split(':')[1])
                .seconds(0),
            closing_time: date
                .clone()
                .hours(opening_hours.close.split(':')[0])
                .minutes(opening_hours.close.split(':')[1])
                .seconds(0),
        };
    },

    /*----------------------------------------------------------------------
     * Returns array of available order methods for the location
     */
    getAvailableOrderMethods() {
        const available_methods = [];
        if (this.order_methods.pickup) {
            available_methods.push('pickup');
        }
        if (this.order_methods.eatin) {
            available_methods.push('eatin');
        }
        if (this.order_methods.delivery) {
            available_methods.push('delivery');
        }
        return available_methods;
    },

    /*----------------------------------------------------------------------
     */
    isOrderMethodAvailable(method) {
        return !!this.order_methods[method];
    },

    /*--------------------------------------------------------------------------
     * Returns array of available payments methods for a given order method
     */
    getAvailablePaymentMethods(order_method) {
        if (!order_method) return [];

        const available_methods = [];

        if (this.payment_methods.online) {
            available_methods.push('online');
        }

        if (!isPayAtTablePlatform()) {
            if (
                (order_method === 'pickup' || order_method === 'eatin') &&
                this.payment_methods.offline_pickup
            ) {
                available_methods.push('offline');
            } else if (
                order_method === 'delivery' &&
                this.payment_methods.offline_delivery
            ) {
                available_methods.push('offline');
            }
        }

        return available_methods;
    },

    /*--------------------------------------------------------------------------
     * Removes addresses location cannot delivery to from a list of addresses
     */
    filterAvailableAddresses(addresses) {
        if (addresses instanceof Mobi2Go._class.Common.Collection) {
            addresses = addresses.toArray();
        }
        return _.filter(addresses, _.bind(this.isAddressAvailable, this));
    },

    /*--------------------------------------------------------------------------
     * Returns whether provided customer address is available for delivery
     * at location
     */
    isAddressAvailable(address) {
        if (this.delivery.mode === 'door_dash_drive') {
            // Let the backend check
            return true;
        }

        if (this.delivery.mode === 'map_polygons') {
            return this.isAddressInDeliveryMapPolygons(address);
        }

        if (this.delivery.mode === 'suburb_list') {
            return this.isAddressInDeliverySuburbList(address);
        }

        if (this.delivery.mode === 'post_code_list') {
            return this.isAddressInPostCodeList(address);
        }
    },

    /*--------------------------------------------------------------------------
     */
    isAddressInDeliverySuburbList(address) {
        if (this.delivery.suburb_list && this.delivery.suburb_list.areas) {
            const { areas } = this.delivery.suburb_list;
            for (const i in areas)
                if (areas.hasOwnProperty(i)) {
                    const area = areas[i];

                    if (
                        (address.suburb_id &&
                            address.suburb_id === area.suburb_id) ||
                        (address.suburb &&
                            address.suburb === area.suburb &&
                            address.city &&
                            address.city === area.city)
                    ) {
                        return true;
                    }
                }
        }

        return false;
    },

    /*--------------------------------------------------------------------------
     */
    getPostCodeRange(post_code) {
        const rangeExp = /^(\d+)-(\d+)$/;
        const matches = post_code.toString().match(rangeExp);
        if (!matches) {
            return null;
        }
        const start = parseInt(matches[1]);
        const end = parseInt(matches[2]);
        if (start > end) {
            return [end, start];
        }
        return [start, end];
    },

    isPostCodeInRange(post_code, range) {
        post_code = parseInt(post_code);
        if (post_code >= range[0] && post_code <= range[1]) {
            return true;
        }
        return false;
    },

    isPostCodeEqual(address_post_code, post_code) {
        address_post_code = address_post_code.toString();
        post_code = post_code.toString();

        if (address_post_code.length !== post_code.length) {
            return false;
        }
        for (let i = 0; i < address_post_code.length; i++) {
            if (address_post_code[i] !== post_code[i] && post_code[i] !== '*') {
                return false;
            }
        }
        return true;
    },

    isAddressInPostCodeList(address) {
        if (this.delivery.post_code_list) {
            const { post_code_list } = this.delivery;
            for (const i in post_code_list)
                if (post_code_list.hasOwnProperty(i)) {
                    const zone = post_code_list[i];
                    if (!zone.post_code) {
                        continue;
                    }

                    const range = this.getPostCodeRange(zone.post_code);

                    if (
                        range &&
                        this.isPostCodeInRange(address.post_code, range)
                    ) {
                        return true;
                    }

                    if (
                        this.isPostCodeEqual(address.post_code, zone.post_code)
                    ) {
                        return true;
                    }
                }
        }

        return false;
    },

    /*--------------------------------------------------------------------------
     */
    isAddressInDeliveryMapPolygons(address) {
        if (!address.latitude || !address.longitude) {
            return false;
        }

        return this.isPointInDeliveryMapPolygons(address);
    },

    /**-------------------------------------------------------------------------
     */
    isPointInDeliveryMapPolygons(point) {
        if (
            !this.delivery.map_polygons ||
            !this.delivery.map_polygons.features
        ) {
            return false;
        }

        const { features } = this.delivery.map_polygons; // GeoJSON
        let is_in_a_polygon = false;

        _.each(features, feature => {
            if (
                is_in_a_polygon ||
                (feature.geometry.type !== 'Polygon' &&
                    feature.geometry.type !== 'Point')
            ) {
                return;
            }
            const maps_service = this.getMapsService();

            if (feature.geometry.type === 'Polygon') {
                const polygon = feature.geometry.coordinates[0];
                is_in_a_polygon = maps_service.isPointInPolygon(point, polygon);
            } else if (feature.geometry.type === 'Point') {
                // Circle
                const zone_point = feature.geometry.coordinates;

                const radius = feature.properties.radius || 0; // In metres
                const metres_between_points =
                    maps_service.getDistanceBetweenPoints(point, zone_point) *
                    1000;

                is_in_a_polygon = metres_between_points <= radius;
            }
        });

        return is_in_a_polygon;
    },

    /*--------------------------------------------------------------------------
     */
    getDoorDashDriveEstimate(address) {
        return new Promise(resolve => {
            if (address === null) {
                resolve(null);
            }

            if (this.delivery.map_polygons?.features?.length) {
                if (!this.isAddressInDeliveryMapPolygons(address)) {
                    resolve(null);
                    return;
                }
            }

            const dropoff_address = {
                city: address.city,
                state: address.region,
                street: `${address.street_number} ${address.street_name}`,
                unit: address.specific_number,
                zip_code: address.post_code,
            };

            // TODO: a better way to mock this
            if (!process.env.production) {
                setTimeout(() => {
                    if (address.suburb === 'Te Aro') {
                        resolve({
                            fee: 10,
                            pickup_time: '',
                            delivery_time: '',
                            wait_time: 10,
                        });
                    } else {
                        resolve(null);
                    }
                }, 2000);
                return;
            }

            Mobi2Go.app.serverRequest({
                endpoint: [
                    'locations',
                    this.id,
                    'delivery',
                    'door_dash_drive',
                    'estimate',
                ],
                type: 'POST',
                data: dropoff_address,
                api_version: 1,
                with_credentials: false,
                success: data => resolve(data),
                error: () => {
                    const error_handled = true;
                    resolve(null);
                    return error_handled;
                },
            });
        });
    },

    /*--------------------------------------------------------------------------
     */
    toOrderDatetime(datetime) {
        if (moment.isMoment(datetime)) {
            datetime = datetime.clone();
        } else {
            datetime = moment.parseZone(datetime);
        }

        datetime.datetime_value = datetime.toISOString(true);
        datetime.date_value = datetime.format(DATE_FORMAT);
        datetime.display_value = Mobi2Go.helpers.formatDatetimeToTimeDisplayValue(
            datetime
        );

        return datetime;
    },

    /*--------------------------------------------------------------------------
     */
    getAvailableMenus() {
        if (Mobi2Go.Menu) {
            if (isPayAtTablePlatform()) {
                const pay_at_table_menu = Mobi2Go.Menu.getMenusByTag(
                    PLATFORMS.PAYATTABLE
                );

                if (pay_at_table_menu) {
                    return pay_at_table_menu;
                }
            }

            const menus = Mobi2Go.Menu.getMenus();

            return _.map(this.menus, function(menu) {
                return menus.find({
                    id: menu.id,
                });
            });
        }
    },

    /*--------------------------------------------------------------------------
     * Returns distance a point and the location in kilometers
     */
    getDistanceFromPoint(point) {
        return this.getMapsService().getDistanceBetweenPoints(
            this.address,
            point
        );
    },

    /*--------------------------------------------------------------------------
     */
    getMapsService() {
        if (this.maps_service) {
            return this.maps_service;
        }

        return (this.maps_service = new Mobi2Go._class.MapsService(this));
    },

    /*--------------------------------------------------------------------------
     * Returns a moment with a supplied date that has the location's timezone
     */
    momentWithTimezone(date) {
        date = moment(date);
        return this.current_time.clone().set({
            year: date.get('year'),
            month: date.get('month'),
            date: date.get('date'),
        });
    },

    /**-------------------------------------------------------------------------
     */
    getAddressString() {
        let string = '';
        const { address } = this;

        switch (address.type) {
            case 'residential': {
                string += address.getResidentialDisplayStreetNumberAndUnit();
                break;
            }
            case 'business': {
                string += address.name ? `${address.name}, ` : '';
                string += address.specific_number
                    ? `Level ${address.specific_number}, `
                    : '';
                string += address.street_number
                    ? `${address.street_number} `
                    : '';
                break;
            }
            case 'apartment': {
                string += address.name ? `${address.name}, ` : '';
                string += address.specific_number
                    ? `Apt. ${address.specific_number}, `
                    : '';
                string += address.street_number
                    ? `${address.street_number} `
                    : '';
                break;
            }
            case 'hotel': {
                string += address.specific_number
                    ? `Room ${address.specific_number}, `
                    : '';
                string += address.name ? `${address.name}, ` : '';
                string += address.street_number
                    ? `${address.street_number} `
                    : '';
                break;
            }
        }

        string += address.street_number || '';
        string += address.street_name
            ? (address.street_number ? ' ' : '') + address.street_name
            : '';
        string += address.suburb ? `, ${address.suburb}` : '';

        if (address.city !== address.suburb) {
            string += address.city ? `, ${address.city}` : '';
        }

        if (address.region !== address.city) {
            string += address.region ? `, ${address.region}` : '';
        }

        return string;
    },

    isGroupTabsEnabled() {
        return this.group_tabs_enabled;
    },

    isPayAtTableEnabled() {
        return this.pay_at_table_enabled;
    },

    isPayAtTableConfigured() {
        return this.pay_at_table_configured;
    },
});

/*------------------------------------------------------------------------------
 * Private methods
 */

/*------------------------------------------------------------------------------
 * Sets opening hours for object - parses opening_hours data to create
 * opening_hours_abbr property which is the opening hours data abbreviated
 * which is easier to read and display
 */

function generateAbbrOpeningHours() {
    if (!this.opening_hours) return;
    // Whether opening hours are the same for all order methods
    const that = this;
    const order_methods = this.getAvailableOrderMethods();
    let oh;

    this.opening_hours_abbr = {};

    if (this.use_new_opening_hours) {
        oh = order_methods.map(order_method =>
            that.opening_hours[order_method]?.serialize()
        );

        if (
            order_methods.length === 1 ||
            (order_methods.length === 2 && _.isEqual(oh[0], oh[1])) ||
            (order_methods.length === 3 &&
                _.isEqual(oh[0], oh[1]) &&
                _.isEqual(oh[0], oh[2]))
        ) {
            this.opening_hours_abbr.all = formatWeekNewOpeningHours(
                this.opening_hours[order_methods[0]]
            );
        } else {
            // Heterogenous opening hours for each order method
            _.each(order_methods, function(method) {
                that.opening_hours_abbr[method] = formatWeekNewOpeningHours(
                    that.opening_hours[method]
                );
            });
        }
    } else {
        oh = this.opening_hours;

        // Homogenous opening hours for all order methods
        if (
            order_methods.length === 1 ||
            (order_methods.length === 2 &&
                _.isEqual(oh[order_methods[0]], oh[order_methods[1]])) ||
            (order_methods.length === 3 &&
                _.isEqual(oh[order_methods[0]], oh[order_methods[1]]) &&
                _.isEqual(oh[order_methods[0]], oh[order_methods[2]]))
        ) {
            this.opening_hours_abbr.all = formatWeekOpeningHours(
                this.opening_hours[order_methods[0]]
            );
        } else {
            // Heterogenous opening hours for each order method
            _.each(order_methods, function(method) {
                that.opening_hours_abbr[method] = formatWeekOpeningHours(
                    that.opening_hours[method]
                );
            });
        }
    }
}

/*------------------------------------------------------------------------------
 * Auto increment current time - just like in real life
 */

const UPDATE_INTERVAL = 1000 * 10;
const current_time_intervals = {};
function startAutoUpdatingCurrentTime(location) {
    clearTimeout(current_time_intervals[location.id]);
    current_time_intervals[location.id] = setInterval(function() {
        if (location.current_time) {
            const new_current_time = location.current_time.add(
                UPDATE_INTERVAL,
                'ms'
            );
            location.setCurrentTime(new_current_time);
        }
    }, UPDATE_INTERVAL);
}

/*------------------------------------------------------------------------------
 * Converts a provided week's opening hours into a more condense format
 * e.g. Mon - Tues 10:00am - 5:00pm,
 *      Wed - Sun 9:00am - 10:00pm
 */
function formatWeekOpeningHours(hours) {
    const display_days = moment.weekdaysShort();
    display_days.push(display_days.shift());
    const lines = [];
    let day;
    let prev_day;
    let i;
    let l = -1;

    for (i = 0; i <= 6; i++) {
        day = DAYS[i];
        prev_day = DAYS[i - 1];
        if (_.isEqual(hours[day], hours[prev_day])) {
            // Existing line
            lines[l][1] = display_days[i];
        } else {
            // New line
            l++;
            lines[l] = [
                display_days[i],
                null,
                formatDayOpeningHours(hours[day]),
            ];
        }
    }
    l++;
    while (l--)
        lines[l] = lines[l][1]
            ? [`${lines[l][0]}–${lines[l][1]}`, lines[l][2]]
            : [lines[l][0], lines[l][2]];

    return lines;
}

function formatWeekNewOpeningHours(opening_hours) {
    const display_days = moment.weekdaysShort();
    display_days.push(display_days.shift());
    const lines = [];
    let l = 0;
    const days = Day.days();

    for (let i = 0; i < 7; i += 1) {
        const day = opening_hours.operating_hours[days[i]];
        const previous_day = opening_hours.operating_hours[days[(6 + i) % 7]];

        if (i > 0 && day.toString() !== previous_day.toString()) {
            l += 1;
        }
        lines[l] = lines[l] || [];
        lines[l].push({
            display_day: display_days[i],
            hours: formatDayNewOpeningHours(day),
        });
    }

    return _.map(lines, function(line) {
        if (line.length === 1) {
            return [line[0].display_day, line[0].hours];
        }
        return [
            `${line[0].display_day}–${line[line.length - 1].display_day}`,
            line[0].hours,
        ];
    });
}

/*------------------------------------------------------------------------------
 */
function formatDayOpeningHours(hours) {
    if (!hours.is_open) {
        return i18n.t('location:closed');
    }
    return `${Mobi2Go.helpers.AMPMise(hours.open)}–${Mobi2Go.helpers.AMPMise(
        hours.close
    )}`;
}

function formatDayNewOpeningHours(hours_for_day) {
    if (hours_for_day.count() === 0) {
        return i18n.t('location:closed');
    }

    return _.map(hours_for_day.getTimeRanges(), function(time_range) {
        return `${i18n
            .t('location:display_time', { time: time_range.start })
            .replace(
                /(0)([0-9]:[0-5][0-9])/,
                '&nbsp;&nbsp;$2'
            )} – ${i18n.t('location:display_time', { time: time_range.end }).replace(/(0)([0-9]:[0-5][0-9])/, '&nbsp;&nbsp;$2')}`;
    }).join('<br />');
}

/*------------------------------------------------------------------------------
 */
function roundMinutes(moment, rounding) {
    const mod = moment.minutes() % rounding;
    if (mod) {
        moment.add(rounding - mod, 'minutes');
    }

    return moment;
}
